import {
  RequestHeaderApiFactory,
  RequestHeaderDetail,
  InlineResponse2001 as ProximitySearchResult,
  InlineResponse200 as BoundarySearchResult,
  RequestHeaderSummaryListPrintResponse,
  RequestHeaderProximityRegister,
} from '@/api'
import { RequestTypes } from '@/constants/enums'
import { addMessageQueue, MESSAGE_TYPE_ERROR } from '@/plugins/messageQueue'
import { BaseInitRequest } from '@/requests/baseRequest'
import {
  RequestHeaderBoundaryDetail,
  RequestHeaderProximityDetail,
  RequestHeaderProximityRegisterEx,
} from '@/types/extendsApi'
import { BoundarySearchConditionForm } from '@/types/forms'
import { AxiosPromise, AxiosResponse } from 'axios'
import Message from '@/services/message'

/**
 * 申請基本取得
 */
export class ReadRequest extends BaseInitRequest {
  private requestHeaderId: number
  constructor(requestHeaderId: number) {
    super()
    this.requestHeaderId = requestHeaderId
  }

  protected request(): AxiosPromise<RequestHeaderDetail> {
    const api = RequestHeaderApiFactory()
    return api.requestHeaderRead(this.requestHeaderId)
  }

  /**
   * 近接工事情報を取得する
   * @returns
   */
  public getProximity(): RequestHeaderProximityDetail {
    const { data } = this.getResponse() as AxiosResponse<RequestHeaderDetail>
    // 申請種別チェック
    if (data.request_type !== RequestTypes.PROXIMITY) {
      throw new Error(`近接工事申請以外の種別です。request_header_id: ${data.request_header_id}`)
    }
    // proximity存在チェック
    if (data.proximity == null) {
      throw new Error(`近接工事情報がありません。request_header_id: ${data.request_header_id}`)
    }

    return data as RequestHeaderProximityDetail
  }

  /**
   * 境界立会情報のレスポンスを取得する
   * @returns
   */
  public getBoundary(): RequestHeaderBoundaryDetail {
    const { data } = this.getResponse() as AxiosResponse<RequestHeaderDetail>

    // 申請種別チェック
    if (data.request_type !== RequestTypes.BOUNDARY) {
      throw new Error(`境界立会申請以外の種別です。request_header_id: ${data.request_header_id}`)
    }
    // proximity存在チェック
    if (data.boundary == null) {
      throw new Error(`境界立会情報がありません。request_header_id: ${data.request_header_id}`)
    }

    return data as RequestHeaderBoundaryDetail
  }

  /**
   * 申請種別の取得
   * @returns
   */
  public getRequestType(): number {
    const { data } = this.getResponse() as AxiosResponse<RequestHeaderDetail>
    if (data.request_type == null) {
      throw new Error(`申請種別がありません。request_header_id: ${data.request_header_id}`)
    }
    return data.request_type
  }
}

/**
 * 近接工事申請一覧取得処理
 */
export class ReadListRequest extends BaseInitRequest {
  private applicantNumber: string | undefined
  private applicantDateStart: string | undefined
  private applicantDateEnd: string | undefined
  private departmentId: number | undefined
  private applicantTitle: string | undefined
  private lineCode: string | undefined
  private meterStart: number | undefined
  private meterEnd: number | undefined
  private stationId: string | undefined
  private judgeDiscussionStatus: string | undefined
  private priorDiscussionStatus: string | undefined
  private discussionDocumentStatus: string | undefined
  private witnessStatus: string | undefined
  private answerStatus: string | undefined
  private adversariaStatus: string | undefined
  private orderBy: string | undefined
  private page: number | undefined
  constructor(
    applicantNumber?: string | null,
    applicantDateStart?: string | null,
    applicantDateEnd?: string | null,
    departmentId?: number | null,
    applicantTitle?: string | null,
    lineCode?: string | null,
    meterStart?: number | null,
    meterEnd?: number | null,
    stationId?: number | null,
    judgeDiscussionStatus?: string | null,
    priorDiscussionStatus?: string | null,
    discussionDocumentStatus?: string | null,
    witnessStatus?: string | null,
    answerStatus?: string | null,
    adversariaStatus?: string | null,
    orderBy?: string | null,
    page?: number | null
  ) {
    super()
    if (applicantNumber != null) {
      this.applicantNumber = applicantNumber
    }
    if (applicantDateStart != null) {
      this.applicantDateStart = applicantDateStart
    }
    if (applicantDateEnd != null) {
      this.applicantDateEnd = applicantDateEnd
    }
    if (departmentId != null) {
      this.departmentId = departmentId
    }
    if (applicantTitle != null) {
      this.applicantTitle = applicantTitle
    }
    if (lineCode != null) {
      this.lineCode = lineCode
    }
    if (meterStart != null) {
      this.meterStart = meterStart
    }
    if (meterEnd != null) {
      this.meterEnd = meterEnd
    }
    if (stationId != null) {
      this.stationId = String(stationId)
    }
    if (judgeDiscussionStatus != null) {
      this.judgeDiscussionStatus = judgeDiscussionStatus
    }
    if (priorDiscussionStatus != null) {
      this.priorDiscussionStatus = priorDiscussionStatus
    }
    if (discussionDocumentStatus != null) {
      this.discussionDocumentStatus = discussionDocumentStatus
    }
    if (witnessStatus != null) {
      this.witnessStatus = witnessStatus
    }
    if (answerStatus != null) {
      this.answerStatus = answerStatus
    }
    if (adversariaStatus != null) {
      this.adversariaStatus = adversariaStatus
    }
    if (orderBy != null) {
      this.orderBy = orderBy
    }
    if (page != null) {
      this.page = page
    }
  }
  protected request(): AxiosPromise<ProximitySearchResult> {
    const api = RequestHeaderApiFactory()
    return api.requestHeaderProximity(
      this.applicantNumber,
      this.applicantDateStart,
      this.applicantDateEnd,
      this.departmentId,
      this.applicantTitle,
      this.lineCode,
      this.meterStart,
      this.meterEnd,
      this.stationId,
      this.judgeDiscussionStatus,
      this.priorDiscussionStatus,
      this.discussionDocumentStatus,
      this.witnessStatus,
      this.answerStatus,
      this.adversariaStatus,
      this.orderBy,
      this.page
    )
  }

  /**
   * 404エラー時の処理
   * エラーメッセージを表示
   */
  protected notFound(): void {
    addMessageQueue(MESSAGE_TYPE_ERROR, Message.codes.NOT_FOUND_SEARCH)
  }
}

/**
 * 境界立会一覧検索
 */
export class BoundaryListRequest extends BaseInitRequest {
  private condition: BoundarySearchConditionForm | undefined
  constructor(condition?: BoundarySearchConditionForm) {
    super()
    this.condition = condition
  }

  protected request(): AxiosPromise<BoundarySearchResult> {
    const api = RequestHeaderApiFactory()

    if (this.condition === undefined) {
      // 検索条件無しで検索
      return api.requestHeaderBoundary()
    } else {
      // 検索条件有りで検索
      return api.requestHeaderBoundary(
        this.condition.applicant_number,
        this.condition.start_applicant_date,
        this.condition.end_applicant_date,
        this.condition.department_id ?? undefined,
        this.condition.applicant_title,
        this.condition.line_code,
        this.numberToStr(this.condition.start_station_id),
        this.condition.meter_start ?? undefined,
        this.condition.meter_end ?? undefined,
        this.booleanToStr(this.condition.is_boundary_confirm_applicant_deceipt_date),
        this.booleanToStr(this.condition.is_research_start_date),
        this.booleanToStr(this.condition.is_boundary_confirm_receipt_date),
        this.booleanToStr(this.condition.is_send_date),
        this.booleanToStr(this.condition.is_land_system_register_date),
        this.condition.applicant_business_name,
        this.condition.applicant_manager_name,
        this.condition.agent_name,
        this.numberToStr(this.condition.request_reason_type),
        this.condition.request_reason_type_other,
        this.condition.location,
        this.condition.jr_site_location,
        this.condition.start_witness_date,
        this.condition.end_witness_date,
        this.numberToStr(this.condition.witness_result_status),
        this.condition.order_by,
        this.condition.page
      )
    }
  }

  /**
   * 404エラー時の処理
   * エラーメッセージを表示
   */
  protected notFound(): void {
    addMessageQueue(MESSAGE_TYPE_ERROR, Message.codes.NOT_FOUND_SEARCH)
  }

  /**
   * 数値を文字列に変換する。nullならundefinedとする
   * @param value
   * @returns
   */
  private numberToStr(value: number | null): string | undefined {
    if (value == null) {
      return undefined
    } else {
      return value.toString()
    }
  }

  /**
   * 真偽値を文字列に変換する。nullならundefinedとする
   * @param value
   * @returns
   */
  private booleanToStr(value: boolean | null): string | undefined {
    if (value === null) {
      return undefined
    }
    return value.toString()
  }

  /**
   * ソート項目定義
   */
  private static COL_ORDERS = Object.freeze([
    Object.freeze({ col_name: 'applicant_number', sort_key: 'applicant_number' }),
    Object.freeze({ col_name: 'applicant_date', sort_key: 'applicant_time' }),
    Object.freeze({
      col_name: 'boundary_confirm_applicant_receipt_date',
      sort_key: 'boundary_request_header__boundary_confirm_applicant_receipt_date',
    }),
    Object.freeze({ col_name: 'research_start_date', sort_key: 'boundary_request_header__research_start_date' }),
    Object.freeze({
      col_name: 'boundary_confirm_receipt_date',
      sort_key: 'boundary_request_header__boundary_confirm_receipt_date',
    }),
    Object.freeze({ col_name: 'send_date', sort_key: 'boundary_request_header__send_date' }),
    Object.freeze({
      col_name: 'land_system_register_date',
      sort_key: 'boundary_request_header__land_system_register_date',
    }),
    Object.freeze({ col_name: 'department_name', sort_key: 'department__department_sort' }),
    Object.freeze({ col_name: 'applicant_title', sort_key: 'applicant_title' }),
    Object.freeze({ col_name: 'line_name', sort_key: 'boundary_request_header__line__line_sort' }),
    Object.freeze({ col_name: 'display_meter', sort_key: 'meter' }),
    Object.freeze({ col_name: 'start_station_name', sort_key: 'boundary_request_header__start_station__station_sort' }),
    Object.freeze({ col_name: 'end_station_name', sort_key: 'boundary_request_header__end_station__station_sort' }),
    Object.freeze({ col_name: 'applicant_business_name', sort_key: 'applicant_business_name' }),
    Object.freeze({ col_name: 'location', sort_key: 'boundary_request_header__location' }),
    Object.freeze({ col_name: 'jr_site_location', sort_key: 'boundary_request_header__jr_site_location' }),
    Object.freeze({ col_name: 'witness_date', sort_key: 'boundary_request_header__witness_date' }),
    Object.freeze({ col_name: 'applicant_manager_name', sort_key: 'applicant_manager_name' }),
    Object.freeze({
      col_name: 'witness_result_status_name',
      sort_key: 'boundary_request_header__witness_result_status',
    }),
  ])

  /** デフォルト並び順 */
  public static DEFAULT_ORDER_BY = '-applicant_time'

  /**
   * 並び順の指定文字列を返す
   * @param colName
   * @param desc
   * @returns
   */
  public static getOrderBy(colName: string, desc: boolean): string {
    // col名を検索
    const orderInfo = BoundaryListRequest.COL_ORDERS.find((order) => order.col_name === colName)
    if (orderInfo == null) {
      // 無ければデフォルト
      return BoundaryListRequest.DEFAULT_ORDER_BY
    }
    // あれば返す
    return (desc ? '-' : '') + orderInfo.sort_key
  }

  /**
   * order_byからカラム名、並び順を取得する
   * @param orderBy
   * @returns
   */
  public static splitOrderBy(orderBy: string): { prop: string; order: string } {
    // col名を検索
    const orderInfo = BoundaryListRequest.COL_ORDERS.find((order) => {
      return orderBy.match(new RegExp(`-?${order.sort_key}`))
    })
    if (orderInfo == null) {
      // 存在しない場合、デフォルト
      return {
        prop: 'applicant_date',
        order: 'descending',
      }
    }

    const order = orderBy.startsWith('-') ? 'descending' : 'ascending'
    const prop = orderInfo.col_name
    return {
      prop: prop,
      order: order,
    }
  }
}
/**
 * 近接工事申請一覧取得処理
 */
export class PrintCreateRequest extends BaseInitRequest {
  private applicantNumber: string | undefined
  private applicantDateStart: string | undefined
  private applicantDateEnd: string | undefined
  private departmentId: number | undefined
  private applicantTitle: string | undefined
  private lineCode: string | undefined
  private meterStart: number | undefined
  private meterEnd: number | undefined
  private stationId: string | undefined
  private judgeDiscussionStatus: string | undefined
  private priorDiscussionStatus: string | undefined
  private discussionDocumentStatus: string | undefined
  private witnessStatus: string | undefined
  private answerStatus: string | undefined
  private adversariaStatus: string | undefined
  private orderBy: string | undefined
  private page: number | undefined
  constructor(
    applicantNumber?: string | null,
    applicantDateStart?: string | null,
    applicantDateEnd?: string | null,
    departmentId?: number | null,
    applicantTitle?: string | null,
    lineCode?: string | null,
    meterStart?: number | null,
    meterEnd?: number | null,
    stationId?: number | null,
    judgeDiscussionStatus?: string | null,
    priorDiscussionStatus?: string | null,
    discussionDocumentStatus?: string | null,
    witnessStatus?: string | null,
    answerStatus?: string | null,
    adversariaStatus?: string | null,
    orderBy?: string | null,
    page?: number | null
  ) {
    super()
    if (applicantNumber != null) {
      this.applicantNumber = applicantNumber
    }
    if (applicantDateStart != null) {
      this.applicantDateStart = applicantDateStart
    }
    if (applicantDateEnd != null) {
      this.applicantDateEnd = applicantDateEnd
    }
    if (departmentId != null) {
      this.departmentId = departmentId
    }
    if (applicantTitle != null) {
      this.applicantTitle = applicantTitle
    }
    if (lineCode != null) {
      this.lineCode = lineCode
    }
    if (meterStart != null) {
      this.meterStart = meterStart
    }
    if (meterEnd != null) {
      this.meterEnd = meterEnd
    }
    if (stationId != null) {
      this.stationId = String(stationId)
    }
    if (judgeDiscussionStatus != null) {
      this.judgeDiscussionStatus = judgeDiscussionStatus
    }
    if (priorDiscussionStatus != null) {
      this.priorDiscussionStatus = priorDiscussionStatus
    }
    if (discussionDocumentStatus != null) {
      this.discussionDocumentStatus = discussionDocumentStatus
    }
    if (witnessStatus != null) {
      this.witnessStatus = witnessStatus
    }
    if (answerStatus != null) {
      this.answerStatus = answerStatus
    }
    if (adversariaStatus != null) {
      this.adversariaStatus = adversariaStatus
    }
    if (orderBy != null) {
      this.orderBy = orderBy
    }
    if (page != null) {
      this.page = page
    }
  }
  protected request(): AxiosPromise<RequestHeaderSummaryListPrintResponse> {
    const api = RequestHeaderApiFactory()
    return api.requestHeaderProximitySummaryListPrint(
      this.applicantNumber,
      this.applicantDateStart,
      this.applicantDateEnd,
      this.departmentId,
      this.applicantTitle,
      this.lineCode,
      this.meterStart,
      this.meterEnd,
      this.stationId,
      this.judgeDiscussionStatus,
      this.priorDiscussionStatus,
      this.discussionDocumentStatus,
      this.witnessStatus,
      this.answerStatus,
      this.adversariaStatus,
      this.orderBy,
      this.page
    )
  }

  /**
   * 404エラー時の処理
   * エラーメッセージを表示
   */
  protected notFound(): void {
    addMessageQueue(MESSAGE_TYPE_ERROR, Message.codes.NOT_FOUND_SEARCH)
  }
}
/**
 * 申請者確認用近接工事登録情報取得処理
 */
export class ReadRegisterRequest extends BaseInitRequest {
  private requestHeaderId: number
  constructor(requestHeaderId: number) {
    super()
    this.requestHeaderId = requestHeaderId
  }

  protected request(): AxiosPromise<RequestHeaderProximityRegister> {
    const api = RequestHeaderApiFactory()
    return api.requestHeaderProximityRegister(this.requestHeaderId)
  }

  /**
   * 近接工事情報を取得する
   * @returns
   */
  public getProximity(): RequestHeaderProximityRegisterEx {
    const { data } = this.getResponse() as AxiosResponse<RequestHeaderProximityRegister>
    // 申請種別チェック
    if (data.request_type !== RequestTypes.PROXIMITY) {
      throw new Error(`近接工事申請以外の種別です。request_header_id: ${data.request_header_id}`)
    }
    // proximity存在チェック
    if (data.proximity_register == null) {
      throw new Error(`近接工事情報がありません。request_header_id: ${data.request_header_id}`)
    }

    return data as RequestHeaderProximityRegisterEx
  }
}
